import {ChangeDetectionStrategy, Component, computed, inject, input, output, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule, MatMenuTrigger} from '@angular/material/menu';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {map} from 'rxjs/operators';
import dayjs, {Dayjs} from 'dayjs/esm';

export type DateNavigatorStep = 'day' | 'week' | 'month';

@Component({
  selector: 'xui-date-navigator',
  templateUrl: './date-navigator.component.html',
  styleUrls: ['./date-navigator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatMenuModule, MatDatepickerModule]
})
export class DateNavigatorComponent {
  private breakpointObserver = inject(BreakpointObserver);

  readonly date = input.required<Date | Dayjs>();
  readonly step = input<DateNavigatorStep>('day');
  readonly previousAriaLabel = input<string>('Previous');
  readonly nextAriaLabel = input<string>('Next');
  readonly pickerAriaLabel = input<string>('Pick date');

  readonly dateChanged = output<Date>();
  readonly dateChangedDayjs = output<Dayjs>();

  readonly datePickerTrigger = viewChild<MatMenuTrigger>('datePickerTrigger');

  readonly isHandset = toSignal(
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(map(r => r.matches)),
    {initialValue: false}
  );

  readonly currentDayjs = computed<Dayjs>(() => {
    const value = this.date();
    return dayjs.isDayjs(value) ? (value as Dayjs) : dayjs(value as Date);
  });

  readonly currentDate = computed<Date>(() => this.currentDayjs().toDate());

  readonly label = computed<string>(() => {
    const d = this.currentDayjs();
    return this.isHandset() ? d.format('ddd, D MMM') : d.format('dddd, D MMMM, YYYY');
  });

  previous(): void {
    this.emit(this.currentDayjs().subtract(1, this.step()));
  }

  next(): void {
    this.emit(this.currentDayjs().add(1, this.step()));
  }

  onPickerChanged(newDate: Date | Dayjs): void {
    this.datePickerTrigger()?.closeMenu();
    const next = dayjs.isDayjs(newDate) ? (newDate as Dayjs) : dayjs(newDate as Date);
    this.emit(next);
  }

  private emit(next: Dayjs): void {
    this.dateChanged.emit(next.toDate());
    this.dateChangedDayjs.emit(next);
  }
}
